import {useDispatch} from 'react-redux';
import type {Sorting} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {RepaymentLog} from '@igp/shared/contract/pam';
import {useAsyncActionState} from '@igp/shared/redux/async-actions';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import type {UseListViewEntityProps, UseListViewEntityResult} from '@igp/shared/redux/view';
import {EntityType} from '@models';
import {useViewInit} from '@redux';

import {repaymentLogActions} from './actions';
import {repaymentLogMappingKeys} from './mapper';
import type {
    CreateRepaymentLogPayload,
    EditRepaymentLogPayload,
    RepaymentLogFilterKeys,
    RepaymentLogQueryFields,
    RepaymentLogSortKeys,
    RepaymentLogViewModel,
    RepaymentLogViewModelKeys,
} from './types';

type UseRepaymentLogsProps = Omit<UseListViewEntityProps<RepaymentLogFilterKeys, RepaymentLogViewModelKeys>, 'triggers' | 'ids'>;

type UseRepaymentLogsResult = UseListViewEntityResult<RepaymentLogViewModel, RepaymentLogViewModelKeys>;

export function useRepaymentLogs({
    displayName,
    viewType,
    fields,
    validateFilter,
    defaultFilters,
    defaultPaging,
    defaultSorting,
    cleanDelay,
    isCancelable,
    syncWithUrl,
    realtimeMode,
}: UseRepaymentLogsProps): UseRepaymentLogsResult {
    const mapper = useAutoMapper();
    const queryFileds = fields.map(field =>
        mapper.map<RepaymentLogViewModelKeys, RepaymentLogQueryFields>(
            field,
            repaymentLogMappingKeys.viewModelsKeys,
            repaymentLogMappingKeys.queryFileds
        )
    );
    const {
        items: serverItems,
        totalCount,
        viewEntity,
        forceViewUpdate,
        getSearchFilter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = useViewInit<RepaymentLog, RepaymentLogFilterKeys, RepaymentLogQueryFields, RepaymentLogSortKeys>({
        viewType,
        displayName,
        entity: {
            fields: queryFileds,
            entity: EntityType.RepaymentLog,
        },
        validateFilter,
        defaultFilters,
        defaultPaging,
        defaultSorting: mapSorting(defaultSorting),
        cleanDelay,
        isCancelable,
        syncWithUrl,
        realtime: realtimeMode
            ? {
                  mode: realtimeMode,
                  entity: EntityType.RepaymentLog,
                  triggers: [
                      {
                          type: RealtimeMessageTrigger.Add,
                      },
                      {
                          type: RealtimeMessageTrigger.Delete,
                      },
                      {
                          type: RealtimeMessageTrigger.Update,
                      },
                  ],
              }
            : undefined,
    });

    const items = serverItems.map(item =>
        mapper.tryMap<RepaymentLog, RepaymentLogViewModel>(item, repaymentLogMappingKeys.model, repaymentLogMappingKeys.viewModel)
    );

    function mapSorting(sortings: Sorting<RepaymentLogViewModelKeys>[]): Sorting<RepaymentLogSortKeys>[] {
        return sortings.map(sorting => ({
            field: mapper.map<RepaymentLogViewModelKeys, RepaymentLogSortKeys>(
                sorting.field,
                repaymentLogMappingKeys.viewModelsKeys,
                repaymentLogMappingKeys.sortKeys
            ),
            sort: sorting.sort,
        }));
    }

    function handleVmSortChange(newSortings: Sorting<RepaymentLogViewModelKeys>[]) {
        handleSortChange(mapSorting(newSortings));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        filterString: viewEntity?.filter,
        searchFilter: getSearchFilter(sortField =>
            mapper.map<RepaymentLogSortKeys, RepaymentLogViewModelKeys>(
                sortField,
                repaymentLogMappingKeys.sortKeys,
                repaymentLogMappingKeys.viewModelsKeys
            )
        ),
        forceViewUpdate,
        handlePageChange,
        handlePageSizeChange,
        handleFilterChange,
        handleSortChange: handleVmSortChange,
    };
}

type UseRepaymentLogActionsResult = {
    create: (log: CreateRepaymentLogPayload) => void;
    edit: (log: EditRepaymentLogPayload) => void;
    remove: (repaymentId: string) => void;
    isCreateInProgress: boolean;
    isEditInProgress: boolean;
    isRemoveInProgress: boolean;
};
export function useRepaymentLogActions(): UseRepaymentLogActionsResult {
    const dispatch = useDispatch();
    const {isProgress: isCreateInProgress} = useAsyncActionState(repaymentLogActions.create);
    const {isProgress: isEditInProgress} = useAsyncActionState(repaymentLogActions.edit);
    const {isProgress: isRemoveInProgress} = useAsyncActionState(repaymentLogActions.remove);

    function create(log: CreateRepaymentLogPayload) {
        dispatch(repaymentLogActions.create.request(log));
    }

    function edit(log: EditRepaymentLogPayload) {
        dispatch(repaymentLogActions.edit.request(log));
    }

    function remove(repaymentId: string) {
        dispatch(repaymentLogActions.remove.request(repaymentId));
    }

    return {
        create,
        isCreateInProgress,
        edit,
        isEditInProgress,
        remove,
        isRemoveInProgress,
    };
}
